import type { ReactNode } from 'react';
import { Svg } from '../../primitives/Svg.js';
import { useReducedMotion } from '../../../theme/hooks.js';
import type { IconProps } from '../../primitives/types.js';

export interface IcnUploadProps extends IconProps {
  /** 0..1 determinate upload progress. Omit for ambient hover motion. */
  progress?: number;
  active?: boolean;
}

const CIRC = 2 * Math.PI * 5;

/** Upload — two modes:
 *   - determinate (progress supplied): a ring fills counter-clockwise;
 *   - ambient (default): arrow drifts upward slowly;
 *   - active: arrow bobs with two peaks per cycle for a "uploading…" feel. */
export function IcnUpload(props: IcnUploadProps): ReactNode {
  const { progress, active = false, ...rest } = props;
  const reduced = useReducedMotion();
  const isDeterminate = progress !== undefined;
  const clamped = isDeterminate ? Math.min(1, Math.max(0, progress)) : 0;
  const offset = CIRC * (1 - clamped);
  return (
    <Svg {...rest} iconName="upload">
      <circle cx="8" cy="8" r="5" opacity="0.22" />
      {isDeterminate && (
        <circle
          cx="8"
          cy="8"
          r="5"
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
          transform="rotate(-90 8 8)"
          style={{ transition: 'stroke-dashoffset var(--vq-dur-standard) var(--vq-ease-paper)' }}
        />
      )}
      <g>
        {!reduced && !isDeterminate && active && (
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0;0 -1.4;0 0;0 -0.7;0 0"
            keyTimes="0;0.25;0.5;0.75;1"
            dur="1.4s"
            repeatCount="indefinite"
          />
        )}
        {!reduced && !isDeterminate && !active && (
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0;0 -0.6;0 0"
            dur="3.2s"
            repeatCount="indefinite"
          />
        )}
        <path d="M8 10.5V5" />
        <path d="M5.5 7L8 5l2.5 2" />
      </g>
    </Svg>
  );
}
